import React from "react";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import {
	IGEMImage,
	IGEMImageCol,
	IGEMImagesContainer,
	IGEMImagesRow,
	IGEMListItem,
	IGEMListItemWrapper,
	IGEMListSection,
	IGEMPostDescription,
	IGEMPostImage,
	IGEMPostItemTitle,
	IGEMPostItemWrapper,
	IGEMPostWrapper,
	IGEMSection,
	IGEMTitle,
	ImageOverlayText,
	ImageOverlayWrapper,
} from "@insightfulscience/atomic-react/r1/molecules/iGEM";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import Image from "@insightfulscience/atomic-react/Image";
import SimpleTemplate from "@insightfulscience/atomic-react/SimpleTemplate";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Heading from "@insightfulscience/atomic-react/Heading";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import { publicConfig } from "@src/config";
import HubspotForm from "@src/utils/hubspotForm";
import PreloadImages from "@src/PreloadImages";
import SiteLayout from "../src/SiteLayout";
import resources from "../locales/igem-page";

const IGEM = () => {
	const [t] = useLocale("igem");

	return (
		<>
			<SiteLayout hero>
				<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
					<PageMeta.OGP
						title={t("PAGE_TITLE")}
						description={t("META_DESCRIPTION")}
						locale={t("META_LOCALE")}
						type={t("META_TYPE")}
						url={t("META_URL")}
						image={t("META_IMAGE")}
						imageSecureUrl={t("META_IMAGE_SECURE")}
						siteName={t("META_SITE_NAME")}
						articlePublisher={t("META_ARTICLE_PUBLISHER")}
						isImageBaseUrl
					/>
					<PageMeta.Twitter
						title={t("PAGE_TITLE")}
						description={t("META_DESCRIPTION")}
						site={t("META_TWITTER_SITE")}
						card={t("META_TWITTER_CARD")}
						image={t("META_TWITTER_IMAGE")}
						creator={t("META_TWITTER_CREATOR")}
						isImageBaseUrl
					/>
					<PageMeta.Canonical href={t("META_CANONICAL")} />
					<PageMeta.Author content={t("META_AUTHOR")} />
					<PreloadImages
						imagesHref={[
							"/images/snapgene/igem/sgiGEM.png",
							"/images/snapgene/igem/post1.png",
							"/images/snapgene/igem/post2.png",
							"/images/snapgene/igem/post3.png",
						]}
					/>
				</PageMeta>
				<Hero>
					<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
					<HeroDescription>{t("HERO_SUB_TITLE")}</HeroDescription>
					<HeroAction>
						<HeroButtonLink href="#hubspotIGEMForm">{t("HERO_LINK")}</HeroButtonLink>
					</HeroAction>
				</Hero>
				<IGEMSection>
					<Image src="/images/snapgene/igem/sgiGEM.png" sizing={{ maxW: 80 }} />
				</IGEMSection>
				<IGEMSection>
					<IGEMTitle>{t("POST_SECTION_TITLE")}</IGEMTitle>
				</IGEMSection>
				<IGEMPostWrapper>
					<IGEMPostItemWrapper>
						<IGEMPostItemTitle>{t("POST_TITLE_1")}</IGEMPostItemTitle>
						<IGEMPostImage src="/images/snapgene/igem/post2.png" />
						<IGEMPostDescription>{t("POST_CONTENT_1")}</IGEMPostDescription>
					</IGEMPostItemWrapper>
					<IGEMPostItemWrapper>
						<IGEMPostItemTitle>{t("POST_TITLE_2")}</IGEMPostItemTitle>
						<IGEMPostImage src="/images/snapgene/igem/post1.png" />
						<IGEMPostDescription>{t("POST_CONTENT_2")}</IGEMPostDescription>
					</IGEMPostItemWrapper>
					<IGEMPostItemWrapper>
						<IGEMPostItemTitle>{t("POST_TITLE_3")}</IGEMPostItemTitle>
						<IGEMPostImage src="/images/snapgene/igem/post3.png" />
						<IGEMPostDescription>{t("POST_CONTENT_3")}</IGEMPostDescription>
					</IGEMPostItemWrapper>
				</IGEMPostWrapper>
				<IGEMSection>
					<IGEMTitle>{t("TEAMS_SECTION_TITLE")}</IGEMTitle>
				</IGEMSection>
				<IGEMImagesContainer>
					<IGEMImagesRow>
						<IGEMImageCol className="image-wrapper">
							<IGEMImage src="/images/snapgene/igem/teams1.png" />
							<ImageOverlayWrapper>
								<ImageOverlayText>{t("TEAMS_TEXT_1")}</ImageOverlayText>
								<ImageOverlayText>{t("TEAMS_TEXT_AUTHOR_1")}</ImageOverlayText>
							</ImageOverlayWrapper>
						</IGEMImageCol>
						<IGEMImageCol className="image-wrapper">
							<IGEMImage src="/images/snapgene/igem/teams2.png" />
						</IGEMImageCol>
					</IGEMImagesRow>
					<IGEMImagesRow>
						<IGEMImageCol>
							<IGEMImage src="/images/snapgene/igem/teams3.png" />
						</IGEMImageCol>
						<IGEMImageCol>
							<IGEMImage src="/images/snapgene/igem/teams4.png" />
						</IGEMImageCol>
					</IGEMImagesRow>
					<IGEMImagesRow>
						<IGEMImageCol>
							<IGEMImage src="/images/snapgene/igem/teams5.png" />
						</IGEMImageCol>
						<IGEMImageCol className="image-wrapper">
							<IGEMImage src="/images/snapgene/igem/teams6.png" />
							<ImageOverlayWrapper>
								<ImageOverlayText>{t("TEAMS_TEXT_2")}</ImageOverlayText>
								<ImageOverlayText>{t("TEAMS_TEXT_AUTHOR_2")}</ImageOverlayText>
							</ImageOverlayWrapper>
						</IGEMImageCol>
					</IGEMImagesRow>
				</IGEMImagesContainer>
				<IGEMSection>
					<IGEMTitle>{t("FORM_SECTION_TITLE")}</IGEMTitle>
				</IGEMSection>
				<IGEMListSection>
					<IGEMListItemWrapper>
						<IGEMListItem>{t("LIST_ITEM_1")}</IGEMListItem>
						<IGEMListItem>{t("LIST_ITEM_2")}</IGEMListItem>
						<IGEMListItem>{t("LIST_ITEM_3")}</IGEMListItem>
					</IGEMListItemWrapper>
					<IGEMListItemWrapper>
						<IGEMListItem>{t("LIST_ITEM_4")}</IGEMListItem>
						<IGEMListItem>{t("LIST_ITEM_5")}</IGEMListItem>
					</IGEMListItemWrapper>
					<IGEMListItemWrapper>
						<IGEMListItem>{t("LIST_ITEM_6")}</IGEMListItem>
						<IGEMListItem>{t("LIST_ITEM_7")}</IGEMListItem>
					</IGEMListItemWrapper>
				</IGEMListSection>
				<div id="hubspotFormWrapper">
					<SimpleTemplate utility={{ bgColor: "white" }} cellWidth={11}>
						<Row utility={{ justify: "center" }}>
							<Col sm={12} md={7} spacing={{ mb: 10 }}>
								<Heading Tag="h3" sx={{ fontSize: "l", fontWeight: "normal", textAlign: "center" }}>
									{t("FORM_TITLE")}
								</Heading>
							</Col>
						</Row>
						<HubspotForm id="hubspotIGEMForm" formId={publicConfig.HUBSPOT_IGEM_FORM_ID} />
					</SimpleTemplate>
				</div>
			</SiteLayout>
		</>
	);
};

export default pipe(IGEM, WithLocale({ resources }));
